import React, {
  useState,
} from "react";

import {
  Badge,
  Button,
  Card,
  Col,
  Form,
  Row,
  Spinner,
  Table,
} from "react-bootstrap";

import Papa from "papaparse";
import * as XLSX from "xlsx";

import {
  previewPeajes,
  importarPeajes,
} from "../helpers/rutaPeajes";

/* ==================================================
   HELPERS
================================================== */

const formatoMoneda = (
  value
) => {
  const numero =
    Number(value || 0);

  return numero.toLocaleString(
    "es-AR",
    {
      style: "currency",
      currency: "ARS",
      minimumFractionDigits: 2,
    }
  );
};

const formatoFecha = (
  value
) => {
  if (!value) {
    return "-";
  }

  const d =
    new Date(value);

  if (
    Number.isNaN(
      d.getTime()
    )
  ) {
    return "-";
  }

  return `${String(
    d.getUTCDate()
  ).padStart(
    2,
    "0"
  )}/${String(
    d.getUTCMonth() + 1
  ).padStart(
    2,
    "0"
  )}/${d.getUTCFullYear()}`;
};

const proveedorTexto = (
  value
) => {
  if (
    value ===
    "TELEPEAJE_PLUS"
  ) {
    return "Telepeaje Plus";
  }

  if (
    value ===
    "TELEPASE"
  ) {
    return "TelePASE";
  }

  return value || "-";
};

/* ==================================================
   LEER CSV
================================================== */

const leerCSV = (
  archivo
) => {
  return new Promise(
    (
      resolve,
      reject
    ) => {
      Papa.parse(
        archivo,
        {
          header: true,

          skipEmptyLines:
            true,

          dynamicTyping:
            false,

          delimiter: "",

          complete: (
            resultado
          ) => {
            if (
              resultado?.errors
                ?.length &&
              !resultado?.data
                ?.length
            ) {
              reject(
                new Error(
                  "No se pudo interpretar el archivo CSV."
                )
              );

              return;
            }

            resolve(
              resultado.data ||
                []
            );
          },

          error: (
            error
          ) => {
            reject(
              error
            );
          },
        }
      );
    }
  );
};

/* ==================================================
   LEER XLS / XLSX
================================================== */

const leerExcel = async (
  archivo
) => {
  const buffer =
    await archivo.arrayBuffer();

  const workbook =
    XLSX.read(
      buffer,
      {
        type: "array",
      }
    );

  const hojaNombre =
    workbook.SheetNames[0];

  if (!hojaNombre) {
    throw new Error(
      "El Excel no contiene hojas."
    );
  }

  const hoja =
    workbook.Sheets[
      hojaNombre
    ];

  /*
    Leemos el valor REAL de Excel.
    Así evitamos que XLSX cambie
    día/mes por el formato visual.
  */
  const registros =
    XLSX.utils.sheet_to_json(
      hoja,
      {
        defval: "",
        raw: true,
      }
    );

  const pad2 = (valor) =>
    String(valor).padStart(
      2,
      "0"
    );

  const normalizarFechaExcel =
    (valor) => {
      /*
        Fecha serial de Excel.
      */
      if (
        typeof valor ===
          "number" &&
        valor > 20000
      ) {
        const fecha =
          XLSX.SSF.parse_date_code(
            valor
          );

        if (!fecha) {
          return valor;
        }

        return `${fecha.y}-${pad2(
          fecha.m
        )}-${pad2(
          fecha.d
        )} ${pad2(
          fecha.H || 0
        )}:${pad2(
          fecha.M || 0
        )}:${pad2(
          Math.floor(
            fecha.S || 0
          )
        )}`;
      }

      /*
        Si ya viene como Date.
      */
      if (
        valor instanceof Date &&
        !Number.isNaN(
          valor.getTime()
        )
      ) {
        return `${valor.getFullYear()}-${pad2(
          valor.getMonth() +
            1
        )}-${pad2(
          valor.getDate()
        )} ${pad2(
          valor.getHours()
        )}:${pad2(
          valor.getMinutes()
        )}:${pad2(
          valor.getSeconds()
        )}`;
      }

      /*
        Si viene como texto,
        lo dejamos como está.
        El backend ya sabe
        interpretar fechas texto.
      */
      return valor;
    };

  return registros.map(
    (fila) => {
      const nuevaFila = {
        ...fila,
      };

      const claveFecha =
        Object.keys(
          nuevaFila
        ).find(
          (key) =>
            String(key)
              .trim()
              .toUpperCase()
              .normalize("NFD")
              .replace(
                /[\u0300-\u036f]/g,
                ""
              ) === "FECHA"
        );

      if (claveFecha) {
        nuevaFila[
          claveFecha
        ] =
          normalizarFechaExcel(
            nuevaFila[
              claveFecha
            ]
          );
      }

      return nuevaFila;
    }
  );
};

/* ==================================================
   COMPONENTE
================================================== */

const ImportarPeajes =
  () => {
    const [
      proveedor,
      setProveedor,
    ] = useState("");

    const [
      concesionario,
      setConcesionario,
    ] = useState("");

    const [
      archivo,
      setArchivo,
    ] = useState(null);

    const [
      filas,
      setFilas,
    ] = useState([]);

    const [
      preview,
      setPreview,
    ] = useState(null);

    const [
      leyendoArchivo,
      setLeyendoArchivo,
    ] = useState(false);

    const [
      analizando,
      setAnalizando,
    ] = useState(false);

    const [
      importando,
      setImportando,
    ] = useState(false);

    const [
      error,
      setError,
    ] = useState("");

    const [
      mensaje,
      setMensaje,
    ] = useState("");

    /* ================================================
       LIMPIAR
    ================================================ */

    const limpiar =
      () => {
        setArchivo(null);
        setFilas([]);
        setPreview(null);
        setError("");
        setMensaje("");

        const input =
          document.getElementById(
            "archivoPeajes"
          );

        if (input) {
          input.value = "";
        }
      };

    /* ================================================
       CAMBIO PROVEEDOR
    ================================================ */

    const cambiarProveedor =
      (value) => {
        setProveedor(
          value
        );

        if (
          value !==
          "TELEPASE"
        ) {
          setConcesionario(
            ""
          );
        }

        setPreview(null);
        setMensaje("");
        setError("");
      };

    /* ================================================
       LEER ARCHIVO
    ================================================ */

    const seleccionarArchivo =
      async (event) => {
        const seleccionado =
          event.target
            .files?.[0];

        setArchivo(null);
        setFilas([]);
        setPreview(null);
        setError("");
        setMensaje("");

        if (
          !seleccionado
        ) {
          return;
        }

        try {
          setLeyendoArchivo(
            true
          );

          const nombre =
            seleccionado.name
              .toLowerCase();

          let registros =
            [];

          if (
            nombre.endsWith(
              ".csv"
            )
          ) {
            registros =
              await leerCSV(
                seleccionado
              );
          } else if (
            nombre.endsWith(
              ".xlsx"
            ) ||
            nombre.endsWith(
              ".xls"
            )
          ) {
            registros =
              await leerExcel(
                seleccionado
              );
          } else {
            throw new Error(
              "Formato no permitido. Use CSV, XLS o XLSX."
            );
          }

          if (
            !Array.isArray(
              registros
            ) ||
            registros.length ===
              0
          ) {
            throw new Error(
              "El archivo no contiene registros."
            );
          }

          setArchivo(
            seleccionado
          );

          setFilas(
            registros
          );
        } catch (err) {
          console.error(
            "Error leyendo archivo:",
            err
          );

          setError(
            err?.message ||
              "No se pudo leer el archivo."
          );
        } finally {
          setLeyendoArchivo(
            false
          );
        }
      };

    /* ================================================
       ANALIZAR / PREVIEW
    ================================================ */

    const analizarArchivo =
      async () => {
        setError("");
        setMensaje("");
        setPreview(null);

        if (
          !proveedor
        ) {
          setError(
            "Seleccione TelePASE o Telepeaje Plus."
          );

          return;
        }

        if (
          proveedor ===
            "TELEPASE" &&
          !String(
            concesionario ||
              ""
          ).trim()
        ) {
          setError(
            "Para TelePASE debe indicar el concesionario."
          );

          return;
        }

        if (
          !archivo ||
          filas.length ===
            0
        ) {
          setError(
            "Seleccione un archivo para analizar."
          );

          return;
        }

        try {
          setAnalizando(
            true
          );

          const respuesta =
            await previewPeajes(
              {
                proveedor,

                concesionario:
                  concesionario.trim(),

                nombreArchivo:
                  archivo.name,

                filas,
              }
            );

          if (
            !respuesta?.ok
          ) {
            throw new Error(
              respuesta?.error ||
                "No se pudo analizar el archivo."
            );
          }

          setPreview(
            respuesta
          );
        } catch (err) {
          console.error(
            "Error preview peajes:",
            err
          );

          setError(
            err?.message ||
              "No se pudo analizar el archivo."
          );
        } finally {
          setAnalizando(
            false
          );
        }
      };

    /* ================================================
       IMPORTAR
    ================================================ */

    const confirmarImportacion =
      async () => {
        if (
          !preview ||
          !archivo
        ) {
          return;
        }

        if (
          Number(
            preview.cantidadNuevos ||
              0
          ) === 0
        ) {
          setError(
            "No hay consumos nuevos para importar."
          );

          return;
        }

        const confirma =
          window.confirm(
            `Se importarán ${preview.cantidadNuevos} consumos nuevos por ${formatoMoneda(
              preview.importeNuevos
            )}.\n\n¿Desea continuar?`
          );

        if (
          !confirma
        ) {
          return;
        }

        try {
          setImportando(
            true
          );

          setError("");
          setMensaje("");

          const respuesta =
            await importarPeajes(
              {
                proveedor,

                concesionario:
                  concesionario.trim(),

                nombreArchivo:
                  archivo.name,

                filas,
              }
            );

          if (
            !respuesta?.ok
          ) {
            throw new Error(
              respuesta?.error ||
                "No se pudo realizar la importación."
            );
          }

          const importados =
            Number(
              respuesta
                ?.resultado
                ?.importados ||
                0
            );

          const duplicados =
            Number(
              respuesta
                ?.resultado
                ?.duplicados ||
                0
            );

          setMensaje(
            `Importación realizada correctamente. Nuevos: ${importados}. Duplicados ignorados: ${duplicados}.`
          );

          /*
            Volvemos a pedir preview.
            Ahora deberían aparecer
            como duplicados.
          */

          const nuevoPreview =
            await previewPeajes(
              {
                proveedor,

                concesionario:
                  concesionario.trim(),

                nombreArchivo:
                  archivo.name,

                filas,
              }
            );

          if (
            nuevoPreview?.ok
          ) {
            setPreview(
              nuevoPreview
            );
          }
        } catch (err) {
          console.error(
            "Error importando peajes:",
            err
          );

          setError(
            err?.message ||
              "No se pudo importar el archivo."
          );
        } finally {
          setImportando(
            false
          );
        }
      };

    /* ================================================
       RENDER
    ================================================ */

    return (
      <div className="container-fluid mt-4 mb-4">
        {/* CABECERA */}

        <div className="d-flex flex-wrap justify-content-between align-items-center mb-3">
          <div>
            <h4 className="mb-0">
              Importar Peajes
            </h4>

            <small className="text-muted">
              Importación y control de consumos de TelePASE y Telepeaje Plus
            </small>
          </div>

          <Button
            variant="outline-secondary"
            size="sm"
            className="mt-2"
            onClick={
              limpiar
            }
            disabled={
              analizando ||
              importando ||
              leyendoArchivo
            }
          >
            Limpiar
          </Button>
        </div>

        {/* SELECCIÓN */}

        <Card className="mb-3">
          <Card.Body>
            <Row>
              <Col
                xs={12}
                md={4}
                lg={3}
                className="mb-3"
              >
                <Form.Label>
                  Sistema de peaje
                </Form.Label>

                <Form.Control
                  as="select"
                  size="sm"
                  value={
                    proveedor
                  }
                  onChange={(
                    e
                  ) =>
                    cambiarProveedor(
                      e.target
                        .value
                    )
                  }
                  disabled={
                    analizando ||
                    importando
                  }
                >
                  <option value="">
                    Seleccionar...
                  </option>

                  <option value="TELEPASE">
                    TelePASE
                  </option>

                  <option value="TELEPEAJE_PLUS">
                    Telepeaje Plus
                  </option>
                </Form.Control>
              </Col>

              {proveedor ===
                "TELEPASE" && (
                <Col
                  xs={12}
                  md={4}
                  lg={3}
                  className="mb-3"
                >
                  <Form.Label>
                    Concesionario
                  </Form.Label>

                  <Form.Control
                    type="text"
                    size="sm"
                    placeholder="Ej.: ACCESO NORTE"
                    value={
                      concesionario
                    }
                    onChange={(
                      e
                    ) =>
                      setConcesionario(
                        e.target
                          .value
                      )
                    }
                    disabled={
                      analizando ||
                      importando
                    }
                  />

                  <small className="text-muted">
                    Se identifica según la factura o portal de TelePASE.
                  </small>
                </Col>
              )}

              <Col
                xs={12}
                md={5}
                lg={4}
                className="mb-3"
              >
                <Form.Label>
                  Archivo
                </Form.Label>

                <Form.Control
                  id="archivoPeajes"
                  type="file"
                  size="sm"
                  accept=".csv,.xls,.xlsx"
                  onChange={
                    seleccionarArchivo
                  }
                  disabled={
                    leyendoArchivo ||
                    analizando ||
                    importando
                  }
                />

                <small className="text-muted">
                  Formatos admitidos: CSV, XLS y XLSX.
                </small>
              </Col>

              <Col
                xs={12}
                md={3}
                lg={2}
                className="mb-3 d-flex align-items-end"
              >
                <Button
                  variant="dark"
                  size="sm"
                  block
                  onClick={
                    analizarArchivo
                  }
                  disabled={
                    leyendoArchivo ||
                    analizando ||
                    importando ||
                    !archivo
                  }
                >
                  {analizando ? (
                    <>
                      <Spinner
                        animation="border"
                        size="sm"
                        className="mr-2"
                      />
                      Analizando...
                    </>
                  ) : (
                    "Analizar archivo"
                  )}
                </Button>
              </Col>
            </Row>

            {archivo && (
              <div className="mt-1">
                <small className="text-muted">
                  Archivo seleccionado:{" "}
                  <strong>
                    {
                      archivo.name
                    }
                  </strong>
                  {" — "}
                  {filas.length} filas leídas
                </small>
              </div>
            )}
          </Card.Body>
        </Card>

        {/* ERRORES */}

        {error && (
          <div className="alert alert-danger py-2">
            {error}
          </div>
        )}

        {mensaje && (
          <div className="alert alert-success py-2">
            {mensaje}
          </div>
        )}

        {leyendoArchivo && (
          <div className="text-center py-4">
            <Spinner animation="border" />

            <div className="mt-2">
              Leyendo archivo...
            </div>
          </div>
        )}

        {/* PREVIEW */}

        {preview && (
          <>
            <Card className="mb-3">
              <Card.Body>
                <div className="d-flex flex-wrap justify-content-between align-items-center">
                  <div>
                    <h6 className="mb-1">
                      Resultado del análisis
                    </h6>

                    <small className="text-muted">
                      {proveedorTexto(
                        preview.proveedor
                      )}
                      {" — "}
                      {
                        preview.formatoDetectado
                      }
                    </small>
                  </div>

                  <div className="mt-2">
                    <Badge
                      variant={
                        Number(
                          preview.cantidadNuevos ||
                            0
                        ) >
                        0
                          ? "success"
                          : "secondary"
                      }
                    >
                      {
                        preview.cantidadNuevos
                      }{" "}
                      nuevos
                    </Badge>
                  </div>
                </div>
              </Card.Body>
            </Card>

            {/* KPIs */}

            <Row className="mb-3">
              <Col
                xs={6}
                md={2}
                className="mb-2"
              >
                <Card className="h-100">
                  <Card.Body>
                    <small className="text-muted">
                      Registros
                    </small>

                    <h4 className="mb-0">
                      {
                        preview.cantidadRegistros
                      }
                    </h4>

                    <small className="text-muted">
                      Encontrados
                    </small>
                  </Card.Body>
                </Card>
              </Col>

              <Col
                xs={6}
                md={2}
                className="mb-2"
              >
                <Card className="h-100">
                  <Card.Body>
                    <small className="text-muted">
                      Nuevos
                    </small>

                    <h4 className="mb-0 text-success">
                      {
                        preview.cantidadNuevos
                      }
                    </h4>

                    <small className="text-muted">
                      A importar
                    </small>
                  </Card.Body>
                </Card>
              </Col>

              <Col
                xs={6}
                md={2}
                className="mb-2"
              >
                <Card className="h-100">
                  <Card.Body>
                    <small className="text-muted">
                      Duplicados
                    </small>

                    <h4 className="mb-0 text-warning">
                      {
                        preview.cantidadDuplicados
                      }
                    </h4>

                    <small className="text-muted">
                      Se ignorarán
                    </small>
                  </Card.Body>
                </Card>
              </Col>

              <Col
                xs={6}
                md={2}
                className="mb-2"
              >
                <Card className="h-100">
                  <Card.Body>
                    <small className="text-muted">
                      Vehículos
                    </small>

                    <h4 className="mb-0">
                      {
                        preview.cantidadVehiculos
                      }
                    </h4>

                    <small className="text-muted">
                      Patentes detectadas
                    </small>
                  </Card.Body>
                </Card>
              </Col>

              <Col
                xs={6}
                md={2}
                className="mb-2"
              >
                <Card className="h-100">
                  <Card.Body>
                    <small className="text-muted">
                      Sin vincular
                    </small>

                    <h4
                      className={`mb-0 ${
                        Number(
                          preview.cantidadPatentesNoEncontradas ||
                            0
                        ) >
                          0
                          ? "text-danger"
                          : "text-success"
                      }`}
                    >
                      {
                        preview.cantidadPatentesNoEncontradas
                      }
                    </h4>

                    <small className="text-muted">
                      Patentes no encontradas
                    </small>
                  </Card.Body>
                </Card>
              </Col>

              <Col
                xs={6}
                md={2}
                className="mb-2"
              >
                <Card className="h-100">
                  <Card.Body>
                    <small className="text-muted">
                      Importe nuevo
                    </small>

                    <h5 className="mb-0 text-primary">
                      {formatoMoneda(
                        preview.importeNuevos
                      )}
                    </h5>

                    <small className="text-muted">
                      A incorporar
                    </small>
                  </Card.Body>
                </Card>
              </Col>
            </Row>

            {/* INFORMACIÓN */}

            <Card className="mb-3">
              <Card.Body>
                <Row>
                  <Col
                    xs={12}
                    md={3}
                    className="mb-2"
                  >
                    <small className="text-muted d-block">
                      Desde
                    </small>

                    <strong>
                      {formatoFecha(
                        preview.fechaDesde
                      )}
                    </strong>
                  </Col>

                  <Col
                    xs={12}
                    md={3}
                    className="mb-2"
                  >
                    <small className="text-muted d-block">
                      Hasta
                    </small>

                    <strong>
                      {formatoFecha(
                        preview.fechaHasta
                      )}
                    </strong>
                  </Col>

                  <Col
                    xs={12}
                    md={3}
                    className="mb-2"
                  >
                    <small className="text-muted d-block">
                      Total archivo
                    </small>

                    <strong>
                      {formatoMoneda(
                        preview.importeTotal
                      )}
                    </strong>
                  </Col>

                  <Col
                    xs={12}
                    md={3}
                    className="mb-2"
                  >
                    <small className="text-muted d-block">
                      Sin patente
                    </small>

                    <strong>
                      {
                        preview.cantidadSinPatente
                      }
                    </strong>
                  </Col>
                </Row>
              </Card.Body>
            </Card>

            {/* PATENTES NO ENCONTRADAS */}

            {Array.isArray(
              preview.patentesNoEncontradas
            ) &&
              preview
                .patentesNoEncontradas
                .length > 0 && (
                <div className="alert alert-warning py-2">
                  <strong>
                    Patentes no encontradas en Vehículos:
                  </strong>{" "}
                  {preview.patentesNoEncontradas.join(
                    ", "
                  )}
                </div>
              )}

            {/* TABLA */}

            <Card className="mb-3">
              <Card.Body>
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h6 className="mb-0">
                    Vista previa de consumos
                  </h6>

                  <small className="text-muted">
                    Mostrando hasta 200 registros
                  </small>
                </div>

                <div className="table-responsive">
                  <Table
                    striped
                    hover
                    size="sm"
                  >
                    <thead>
                      <tr>
                        <th>
                          Fecha
                        </th>

                        <th>
                          Hora
                        </th>

                        <th>
                          Patente
                        </th>

                        <th>
                          Vehículo
                        </th>

                        <th>
                          Concesionario
                        </th>

                        <th>
                          Estación
                        </th>

                        <th>
                          Vía
                        </th>

                        <th>
                          Tag
                        </th>

                        <th className="text-right">
                          Importe
                        </th>

                        <th>
                          Estado
                        </th>
                      </tr>
                    </thead>

                    <tbody>
                      {preview
                        .consumos
                        ?.slice(
                          0,
                          200
                        )
                        .map(
                          (
                            item,
                            index
                          ) => (
                            <tr
                              key={`${item.claveUnica}-${index}`}
                            >
                              <td
                                style={{
                                  whiteSpace:
                                    "nowrap",
                                }}
                              >
                                {formatoFecha(
                                  item.fecha
                                )}
                              </td>

                              <td>
                                {item.hora ||
                                  "-"}
                              </td>

                              <td className="font-weight-bold">
                                {item.patente ||
                                  "-"}
                              </td>

                              <td>
                                {item
                                  .vehiculoInfo
                                  ?.camion ||
                                  "-"}
                              </td>

                              <td>
                                {item.concesionario ||
                                  "-"}
                              </td>

                              <td>
                                {item.estacion ||
                                  "-"}
                              </td>

                              <td>
                                {item.via ||
                                  "-"}
                              </td>

                              <td>
                                {item.dispositivo ||
                                  "-"}
                              </td>

                              <td className="text-right font-weight-bold">
                                {formatoMoneda(
                                  item.importeFinal
                                )}
                              </td>

                              <td>
                                {item.esDuplicado ? (
                                  <Badge variant="warning">
                                    Duplicado
                                  </Badge>
                                ) : item.estadoVinculacion ===
                                  "VINCULADO" ? (
                                  <Badge variant="success">
                                    Nuevo
                                  </Badge>
                                ) : (
                                  <Badge variant="danger">
                                    Revisar
                                  </Badge>
                                )}
                              </td>
                            </tr>
                          )
                        )}
                    </tbody>
                  </Table>
                </div>
              </Card.Body>
            </Card>

            {/* IMPORTAR */}

            <div className="d-flex justify-content-end">
              <Button
                variant="success"
                onClick={
                  confirmarImportacion
                }
                disabled={
                  importando ||
                  Number(
                    preview.cantidadNuevos ||
                      0
                  ) === 0
                }
              >
                {importando ? (
                  <>
                    <Spinner
                      animation="border"
                      size="sm"
                      className="mr-2"
                    />

                    Importando...
                  </>
                ) : (
                  `Importar ${preview.cantidadNuevos} consumos nuevos`
                )}
              </Button>
            </div>
          </>
        )}
      </div>
    );
  };

export default ImportarPeajes;